/**
 * compareChartConfig.js : données et options Chart.js de CompareChart.
 *
 * Fonction pure, appelée sous useMemo par CompareChart : react-chartjs-2
 * recopie sur le graphique tout objet nouveau, ce qui annule le zoom ; les
 * objets ne sont donc recréés que si un paramètre change (10.5.336).
 */
import { formatClockTick, clockTicks } from './compareAxis';
import { getComparePalette, dashFor, SESSION_LINE_WIDTH } from '../constants/comparePalette';
import { formatNumberLocale, numberLocale } from '../utils/formatters';
import { semanticColors } from '../constants/semanticColors';

// Chart.js dessine en dernier (donc au-dessus) le dataset d'`order` le plus
// bas ; sessions à 0 par défaut. Sans cela, une courbe qui longe un seuil
// (épaisseur 2) masque ses tirets fins.
export const THRESHOLD_ORDER = -1;

// Points gardés par série décimée : plus que la largeur utile d'un graphique.
const DECIMATION_SAMPLES = 1500;

export function buildCompareChartConfig({ sessions, labels, seriesData, bounds, isClock, decimate, singleDay,
  thresholds, yLabel, darkMode, c, lineStyles, devicePixelRatio, decimals, markZoomed, t, analysisWindow }) {
  const palette = getComparePalette(darkMode);
  const datasets = sessions.map((s, i) => ({
    label: s.session.title || t('sessions.compare.fallbackName', { n: i + 1 }),
    data: seriesData[i] || [],
    borderColor: palette.styleAt(i).color,
    backgroundColor: palette.styleAt(i).fill,
    borderDash: dashFor(palette.styleAt(i), lineStyles),
    borderWidth: SESSION_LINE_WIDTH,
    pointRadius: 0,
    pointHoverRadius: 4,
    tension: 0.3,
    fill: false,
  }));

  // Lignes de seuil : une valeur par minute (fromStart), un segment d'une borne
  // à l'autre (clock).
  const thresholdData = (value) => {
    if (!isClock) return (labels || []).map(() => value);
    return bounds ? [{ x: bounds.min, y: value }, { x: bounds.max, y: value }] : [];
  };
  const thresholdDatasets = thresholds.map(({ value, color, label }) => ({
    label,
    data: thresholdData(value),
    borderColor: color,
    borderWidth: 1,
    borderDash: [5, 4],
    pointRadius: 0,
    fill: false,
    tension: 0,
    showLine: true,
    order: THRESHOLD_ORDER,
  }));

  // Étendue complète de l'axe, et zones hors plage grisées (visibles en dézoomant).
  const fullX = isClock ? bounds : { min: 0, max: Math.max(0, (labels || []).length - 1) };
  const outside = semanticColors.neutralSolid + '33';
  const outOfRange = analysisWindow && fullX ? [
    [fullX.min, analysisWindow.min], [analysisWindow.max, fullX.max],
  ].filter(([a, b]) => b > a).reduce((acc, [xMin, xMax], i) => ({
    ...acc,
    [`outOfRange${i}`]: { type: 'box', xMin, xMax, backgroundColor: outside, borderWidth: 0, drawTime: 'beforeDatasetsDraw' },
  }), {}) : {};

  const axisFont = { family: 'Inter, sans-serif', size: 11 };
  const xScale = isClock
    ? {
        type: 'linear',
        min: analysisWindow?.min ?? bounds?.min,
        max: analysisWindow?.max ?? bounds?.max,
        title: { display: true, text: t('sessions.compare.chart.xAxisClock'), color: c.text, font: axisFont },
        // Graduations rondes recalculées à chaque zoom (scale.min/max courants).
        afterBuildTicks: (scale) => { scale.ticks = clockTicks(scale.min, scale.max); },
        ticks: { color: c.text, font: { size: 10 },
          callback: (v) => formatClockTick(v, singleDay) },
        grid: { color: c.grid },
      }
    : {
        ...(analysisWindow ? { min: analysisWindow.min, max: analysisWindow.max } : {}),
        title: { display: true, text: t('sessions.compare.chart.xAxis'), color: c.text, font: axisFont },
        ticks: { color: c.text, maxTicksLimit: 12, font: { size: 10 },
          callback: (v) => t('sessions.compare.chart.min', { v }) },
        grid: { color: c.grid },
      };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    locale: numberLocale(),
    // Absent : résolution de l'écran (comportement par défaut de Chart.js).
    ...(devicePixelRatio ? { devicePixelRatio } : {}),
    // Décimation : séries {x, y} déjà triées, condition du plugin Chart.js.
    ...(decimate ? { parsing: false } : {}),
    // Heure réelle : les sessions n'ont pas les mêmes abscisses, l'infobulle
    // retient la mesure la plus proche sur l'axe des temps.
    interaction: isClock
      ? { mode: 'nearest', axis: 'x', intersect: false }
      : { mode: 'index', intersect: false },
    scales: {
      x: xScale,
      y: {
        title: { display: true, text: yLabel, color: c.text, font: axisFont },
        ticks: { color: c.text, font: { size: 10 } },
        grid: { color: c.grid },
        beginAtZero: true,
      },
    },
    plugins: {
      legend: { display: false }, // légende custom dans le parent
      tooltip: {
        backgroundColor: c.tooltip,
        titleColor: c.text,
        bodyColor: c.text,
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
        borderWidth: 1,
        // Seuils hors infobulle : leur valeur figure déjà dans la légende.
        filter: (item) => item.datasetIndex < sessions.length,
        callbacks: {
          title: (items) => (isClock
            ? formatClockTick(items[0].parsed.x, singleDay)
            : t('sessions.compare.chart.min', { v: items[0].label })),
          label: (item) => {
            const v = formatNumberLocale(item.parsed.y, decimals);
            return `${item.dataset.label}: ${v ?? t('common.notMeasured')}`;
          },
        },
      },
      zoom: {
        zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x', onZoomComplete: markZoomed },
        pan:  { enabled: true, mode: 'x', onPanComplete: markZoomed },
      },
      annotation: { annotations: outOfRange },
      decimation: decimate
        ? { enabled: true, algorithm: 'lttb', samples: DECIMATION_SAMPLES }
        : { enabled: false },
    },
  };

  const data = isClock
    ? { datasets: [...datasets, ...thresholdDatasets] }
    : { labels, datasets: [...datasets, ...thresholdDatasets] };
  return { data, options };
}
